import {
  Campaign,
  CampaignAlreadyExistsError,
  CampaignNotFoundError,
  CommunicationChannel,
  Contact,
  UpdateCampaignDto,
} from './campaigns.interface';
import {
  getUiTemplateParamsFromValue,
  buildCampaignFromWorkflow,
  buildWorkflowFromCampaign,
} from './campaigns.helpers';
import {
  WORKFLOW_TO_NUMBER_PARAM,
  WORKFLOW_TO_EMAIL_PARAM,
} from './campaigns.helpers';
import {
  CreateWorkflowDto,
  ExecutionDeduplicationConfigDto,
  BatchCreateWorkflowExecutionResponseDto,
  WorkflowWithStatus,
  BatchCreateWorkflowExecutionDto,
  ExecutionDeduplicationAction,
  DayOfWeek,
} from 'src/interfaces/workflows.interface';
import { AuthedRequest } from 'src/hooks/useAuthedRequest';

export const UTC_DAY_TO_DAY_OF_WEEK_MAPPING: Record<number, DayOfWeek> = {
  0: DayOfWeek.sunday,
  1: DayOfWeek.monday,
  2: DayOfWeek.tuesday,
  3: DayOfWeek.wednesday,
  4: DayOfWeek.thursday,
  5: DayOfWeek.friday,
  6: DayOfWeek.saturday,
};

export const DAY_OF_WEEK_TO_UTC_DAY_MAPPING: Record<DayOfWeek, number> = {
  [DayOfWeek.sunday]: 0,
  [DayOfWeek.monday]: 1,
  [DayOfWeek.tuesday]: 2,
  [DayOfWeek.wednesday]: 3,
  [DayOfWeek.thursday]: 4,
  [DayOfWeek.friday]: 5,
  [DayOfWeek.saturday]: 6,
};

export function getCampaignAgentIds(campaign: Campaign): string[] {
  const initialAgentId = campaign.initialCallConfig.agentId;
  const followUpAgentIds = campaign.followUpCommunicationConfigs
    .filter((config) => config.contactVia === CommunicationChannel.CALL)
    .map((config) => config.callConfig!.agentId);

  return [initialAgentId, ...followUpAgentIds];
}

export const getCampaignContextParams = (campaign: Campaign): string[] => {
  const contextParams: Array<string> = [];

  const emailCampaignConfig = campaign.emailCampaignConfig;
  if (emailCampaignConfig) {
    contextParams.push(
      ...getUiTemplateParamsFromValue(emailCampaignConfig.fromEmail),
    );
  }

  const smsCampaignConfig = campaign.smsCampaignConfig;
  if (smsCampaignConfig) {
    contextParams.push(
      ...getUiTemplateParamsFromValue(smsCampaignConfig.fromNumber),
    );
  }

  campaign.followUpCommunicationConfigs.forEach((config) => {
    // TODO: With this implementation the code for SMS and email is not centralized.
    if (config.contactVia === CommunicationChannel.EMAIL) {
      const emailConfig = config.emailConfig!;
      contextParams.push(...getUiTemplateParamsFromValue(emailConfig.subject));
      contextParams.push(...getUiTemplateParamsFromValue(emailConfig.body));
    } else if (config.contactVia === CommunicationChannel.SMS) {
      const smsConfig = config.smsConfig!;
      contextParams.push(...getUiTemplateParamsFromValue(smsConfig.body));
    }
  });

  return Array.from(new Set(contextParams));
};

export const createCampaignsService = (authedRequest: AuthedRequest) => {
  /**
   * Workflow service API methods.
   */
  const fetchWorkflow = async (
    referenceId: string,
  ): Promise<WorkflowWithStatus> => {
    return (await authedRequest.get(
      `/workflows/${referenceId}`,
    )) as WorkflowWithStatus;
  };

  const fetchWorkflows = async (filters?: {
    label?: string;
  }): Promise<{ items: WorkflowWithStatus[] }> => {
    const queryParams = new URLSearchParams();

    if (filters) {
      const workflowFilters: Record<string, string> = {};
      if (filters.label) workflowFilters.label = filters.label;

      if (Object.keys(workflowFilters).length > 0) {
        queryParams.append('filters', JSON.stringify(workflowFilters));
      }
    }

    const url =
      queryParams.toString().length > 0
        ? `/workflows?${queryParams.toString()}`
        : '/workflows';

    return (await authedRequest.get(url)) as {
      items: WorkflowWithStatus[];
    };
  };

  const createWorkflow = async (
    workflow: CreateWorkflowDto,
  ): Promise<WorkflowWithStatus> => {
    return authedRequest.post(`/workflows`, workflow);
  };

  const patchWorkflow = async (
    workflow: CreateWorkflowDto,
  ): Promise<WorkflowWithStatus> => {
    return authedRequest.patch(`/workflows/${workflow.referenceId}`, workflow);
  };

  const deleteWorkflow = async (referenceId: string): Promise<void> => {
    await authedRequest.remove(`/workflows/${referenceId}`);
  };

  const executeWorkflow = async ({
    referenceId,
    params,
  }: {
    referenceId: string;
    params: Record<string, unknown>;
  }): Promise<void> => {
    await authedRequest.post('/workflow_executions', {
      workflowReferenceId: referenceId,
      params,
    });
  };

  const batchExecuteWorkflow = async ({
    referenceId,
    executions,
    deduplicationConfig,
  }: {
    referenceId: string;
    executions: Record<string, unknown>[];
    deduplicationConfig?: ExecutionDeduplicationConfigDto;
  }): Promise<BatchCreateWorkflowExecutionResponseDto> => {
    const payload: BatchCreateWorkflowExecutionDto = {
      workflowReferenceId: referenceId,
      executions: executions.map((execution) => ({
        params: execution,
      })),
      deduplicationConfig,
    };
    return authedRequest.post('/workflow_executions/batch', payload);
  };

  const pauseWorkflow = async (
    referenceId: string,
  ): Promise<WorkflowWithStatus> => {
    return authedRequest.post(`/workflows/${referenceId}/pause`, {});
  };

  const unpauseWorkflow = async (
    referenceId: string,
  ): Promise<WorkflowWithStatus> => {
    return authedRequest.post(`/workflows/${referenceId}/unpause`, {});
  };

  /**
   * Campaign service API methods.
   *
   * Campaigns service is a wrapper around the workflow service.
   */
  async function fetchAllCampaigns(filters?: {
    name?: string;
  }): Promise<Campaign[]> {
    const workflows = await fetchWorkflows(
      filters?.name ? { label: filters.name } : undefined,
    );
    return workflows.items.map(buildCampaignFromWorkflow);
  }

  async function fetchCampaign(referenceId: string): Promise<Campaign> {
    const workflow = await fetchWorkflow(referenceId);
    if (!workflow) {
      throw new CampaignNotFoundError();
    }
    return buildCampaignFromWorkflow(workflow);
  }

  async function createCampaign(
    campaign: UpdateCampaignDto,
  ): Promise<Campaign> {
    let existingWorkflow;
    try {
      existingWorkflow = await fetchWorkflow(campaign.referenceId);
    } catch {
      // Not found
    }

    if (existingWorkflow) {
      throw new CampaignAlreadyExistsError(campaign.referenceId);
    }
    const workflow = buildWorkflowFromCampaign(campaign);
    const savedWorkflow = await createWorkflow(workflow);
    return buildCampaignFromWorkflow(savedWorkflow);
  }

  async function updateCampaign(
    campaign: UpdateCampaignDto,
  ): Promise<Campaign> {
    const workflow = buildWorkflowFromCampaign(campaign);
    const savedWorkflow = await patchWorkflow(workflow);
    return buildCampaignFromWorkflow(savedWorkflow);
  }

  async function addContactToCampaign({
    referenceId,
    contact,
  }: {
    referenceId: string;
    contact: Contact;
  }): Promise<void> {
    const params: Record<string, unknown> = {
      [WORKFLOW_TO_NUMBER_PARAM]: contact.toNumber,
      [WORKFLOW_TO_EMAIL_PARAM]: contact.toEmail,
      context: contact.context,
    };

    await executeWorkflow({
      referenceId,
      params,
    });
  }

  async function batchAddContactsToCampaign({
    referenceId,
    contacts,
    deduplicate,
  }: {
    referenceId: string;
    contacts: Contact[];
    deduplicate: boolean;
  }): Promise<BatchCreateWorkflowExecutionResponseDto> {
    const executionParamsList = contacts.map((contact) => ({
      [WORKFLOW_TO_NUMBER_PARAM]: contact.toNumber,
      [WORKFLOW_TO_EMAIL_PARAM]: contact.toEmail,
      context: contact.context,
    }));
    const deduplicationConfig: ExecutionDeduplicationConfigDto | undefined =
      deduplicate
        ? {
            uniqueParamKeys: [WORKFLOW_TO_NUMBER_PARAM],
            onDuplicate: ExecutionDeduplicationAction.SKIP,
          }
        : undefined;

    return batchExecuteWorkflow({
      referenceId,
      executions: executionParamsList,
      deduplicationConfig,
    });
  }

  async function activateCampaign(referenceId: string): Promise<Campaign> {
    const workflow = await unpauseWorkflow(referenceId);
    return buildCampaignFromWorkflow(workflow);
  }

  async function deactivateCampaign(referenceId: string): Promise<Campaign> {
    const workflow = await pauseWorkflow(referenceId);
    return buildCampaignFromWorkflow(workflow);
  }

  async function deleteCampaign(referenceId: string): Promise<void> {
    await deleteWorkflow(referenceId);
  }

  return {
    fetchAllCampaigns,
    fetchCampaign,
    createCampaign,
    updateCampaign,
    addContactToCampaign,
    batchAddContactsToCampaign,
    activateCampaign,
    deactivateCampaign,
    deleteCampaign,
  };
};
